import Core from "@/core/Core";
import Person from "@/domains/Persons/Person";

import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import PersonsDAO from "@/domains/Persons/PersonsDAO";

import type Partner from "@/domains/Partners/Partner";

class PersonsService {
  static async searchPersons(
    pattern: string,
    partnerId?: number
  ): Promise<Person[]> {
    const response = await PersonsDAO.searchPersons(pattern, partnerId);
    return Core.utils.hydrateArray(response.data, Person.fromServer);
  }

  /**
   * Источник ответственных: персоны партнёра, по умолчанию — текущего.
   * Админ работает от лица поставщика или покупателя — тогда партнёр передаётся
   * геттером, и источник перезагружается при его смене.
   *
   * Персон немного, поэтому грузим список целиком, а VSelectV2 ищет по нему
   * локально. Разрастётся справочник — правим здесь, места использования не трогаем.
   */
  static responsibleSource(
    isNewDocument: boolean,
    getPartner: () => Partner = () => Core.currentPartner,
  ) {
    return Core.loader.select(({ partner, pattern }) => this.searchPersons(pattern, partner.id))
      .dependsOn({ partner: getPartner })
      .preloadOnMount(isNewDocument === true)
      .preloadOnFocus(isNewDocument === false);
  }

  static async get(id: number): Promise<Person | null> {
    const response = await PersonsDAO.get(id);
    return Core.utils.isObject(response.data) ? Person.fromServer(response.data) : null;
  }

  static async sendInvite(person: Person): Promise<boolean> {
    const response = await PersonsDAO.sendInvite(person);

    if (response.ok) {
      Core.showNotification.success("Приглашение выслано");
    }

    return response.ok;
  }

  /** ------- CRUD ------- */

  static async add(person: Person, partnerId: number): Promise<Person | null> {
    const response = await PersonsDAO.add(person, partnerId);

    if (response.ok) {
      Core.showNotification.success("Контактное лицо успешно создано");
    }

    return Core.utils.isObject(response.data) ? Person.fromServer(response.data) : null;
  }

  /** несколько уникальный случай, т.к. на update тут приходит объект */

  static async update(person: Person): Promise<Person | null> {
    const response = await PersonsDAO.update(person);

    if (response.ok) {
      Core.showNotification.success("Успешно обновлено");
    }

    return Core.utils.isObject(response.data) ? Person.fromServer(response.data) : null;
  }

  static async delete(id: number): Promise<boolean> {
    const response = await PersonsDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success("Контакт удалён");
    }

    return response.ok;
  }

  /** ------- вызов диалога создания/редактирования контактного лица ------- */

  static createPerson(
    partnerId: number,
    onPersonCreated: (data?: any) => void,
  ) {
    const person = Person.fromPartner(partnerId);
    this.showPersonDialog(person, onPersonCreated, null);
  }

  static async editPerson(
    personId: number,
    onPersonUpdated: (data?: any) => void,
  ) {
    const fullPerson = await this.get(personId);

    if (!fullPerson) return;

    this.showPersonDialog(fullPerson, null, onPersonUpdated);
  }

  static async deletePerson(person: Person, onPersonDeleted?: () => void) {
    if (await Core.confirm(
      'Удалить это контактное лицо?',
      `${person.name}`
    )) {
      await this.delete(person.id);

      if (onPersonDeleted) {
        onPersonDeleted();
      }
    }
  }

  static showPersonDialog(
    person: Person,
    onPersonCreated: (data?: any) => void,
    onPersonUpdated: (data?: any) => void,
  ) {
    Core.dialogs.PersonCard.show(
      {
        person,
      },
      {
        [Person.Events.Updated]: onPersonUpdated,
        [Person.Events.Created]: onPersonCreated,
      }
    );
  }

  /** секция не грид, поэтому там явная кнопка вызова компонента,
   * а диалогу доступен :context-menu,
   * каждому своё
   * */

  /** ------- контекстное меню контактного лица ------- */

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (person: Person) => { return PersonsService.editPerson(person.id, () => {}); },
        null,
        false,
        false,
        true,
      ),

      new ContextMenuItem(
        'Выслать приглашение',
        'send',
        (person: Person) => { return PersonsService.sendInvite(person); },
        null,
        false,
        false,
      ),

      new ContextMenuItem(
        'Удалить',
        'delete',
        (person: Person) => { return PersonsService.deletePerson(person); },
        null,
        false,
        true,
      ),
    ];
  }
}

export default PersonsService;